import type { FetchArgType } from "openapi-typescript-fetch";
import type {
    GetListParams,
    GetListResult,
    GetManyReferenceParams,
    GetManyReferenceResult,
    UpdateParams,
    UpdateManyParams,
    CreateParams,
    DeleteManyParams,
    DeleteManyResult,
    UpdateResult,
    GetManyResult,
    CreateResult
} from "react-admin";
import { isEmpty } from "lodash";

import { fetcher } from "../../data";
import { isDefined } from "../../utils/typedLoDashUtils";
import { assertIsDefined } from "../../utils/assertions";
import type { BaseFilters } from "../../data/BaseFilters";
import { NotImplementedError } from "../../utils/NotImplementedError";
import type { ErrorDetails, LangParams, MpmDataProvider, MpmDataProviderOperationName } from "../../data";

import type { ArtistDTO } from "./ArtistDTO";
import type { ArtistRecord } from "./ArtistRecord";

const findAll = fetcher.path("/api/content/artist/findAll").method("post").create();
const getOne = fetcher.path("/api/content/artist/{uuid}").method("get").create();
const createOne = fetcher.path("/api/content/artist/").method("post").create();
const updateOne = fetcher.path("/api/content/artist/{uuid}").method("put").create();
const deleteOne = fetcher.path("/api/content/artist/{uuid}").method("delete").create();
const getAllByUuids = fetcher.path("/api/content/artist/allByUuids").method("get").create();

/**
 * Parses the DTO from backend to record for frontend
 * @param dto
 * @returns ArtistRecord
 */

function mapDTOToRecord(dto: ArtistDTO): ArtistRecord {
    // DTO from backend
    const { shortId = 0, uuid = "", fullName = "" } = dto;

    // Record for front-end
    return {
        shortId,
        id: uuid,
        uuid,
        fullName
    };
}

/**
 * Parses the record from frontend to DTO for backend
 * @param record
 * @returns ArtistDTO
 */

function mapRecordToDTO(record: ArtistRecord): ArtistDTO {
    // Record from front-end
    const { shortId = 0, uuid = "", fullName = "" } = record;

    // DTO for back-end
    return {
        id: shortId,
        fullName,
        uuid
    };
}

interface UserFilters extends BaseFilters {
    q?: string;
    email: string;
}

async function getList(params: Readonly<GetListParams>): Promise<GetListResult<ArtistRecord>> {
    const { q: searchTerms, ...filters } = params.filter as UserFilters;

    let parameters: FetchArgType<typeof findAll> = {
        filter: filters,
        pageable: {
            page: params.pagination.page - 1,
            size: params.pagination.perPage,
            sort: isEmpty(params.sort.field) ? [] : [`${params.sort.field},${params.sort.order}`]
        }
    } as const;

    if (isDefined(searchTerms)) {
        parameters = {
            ...parameters,
            filter: { ...parameters.filter, searchTerms }
        };
    }

    const { data } = await findAll(parameters);
    const dataPage = data.content;
    assertIsDefined(dataPage);
    const total = dataPage.totalElements;
    assertIsDefined(total);
    assertIsDefined(dataPage.content);

    return {
        data: dataPage.content.map((content) => mapDTOToRecord({ id: content.id ?? 0, ...content })),
        total
    };
}

async function _getOne(params: LangParams | (number | string)): Promise<ArtistRecord> {
    const {
        data: { content: userDTO }
    } = await getOne({
        uuid: params as string
    });
    assertIsDefined(userDTO);

    return mapDTOToRecord({ id: userDTO.id ?? 0, ...userDTO });
}

async function getMany(uuids: readonly (number | string)[]): Promise<GetManyResult<ArtistRecord>> {
    const {
        data: { content: userDTO }
    } = await getAllByUuids({
        uuids: uuids as string[]
    });
    assertIsDefined(userDTO);

    return { data: await Promise.all(userDTO.map((dto) => mapDTOToRecord({ id: dto.id ?? 0, ...dto }))) };
}

async function getManyReference(_params: Readonly<GetManyReferenceParams>): Promise<GetManyReferenceResult<ArtistRecord>> {
    return Promise.reject(new NotImplementedError("getManyReference"));
}

async function update(params: Readonly<UpdateParams<ArtistRecord>>): Promise<UpdateResult<ArtistRecord>> {
    const payload = mapRecordToDTO(params.data as ArtistRecord);

    const parameters = { uuid: payload.uuid ?? "", ...payload };

    const { data } = await updateOne(parameters);
    const artistDTO = data.content;
    assertIsDefined(artistDTO);

    return { data: mapDTOToRecord({ id: parameters.id, ...artistDTO }) };
}

async function updateMany(_params: Readonly<UpdateManyParams>): Promise<string[]> {
    return Promise.reject(new NotImplementedError("updateMany"));
}

async function create(params: Readonly<CreateParams>): Promise<CreateResult<ArtistRecord>> {
    const payload = mapRecordToDTO(params.data as any);

    assertIsDefined(payload.fullName);
    const parameters = { ...payload };
    delete (parameters as any)["id"];
    delete (parameters as any)["uuid"];
    const { data } = await createOne(parameters);
    const artistDTO = data.content;

    assertIsDefined(artistDTO);

    return { data: mapDTOToRecord({ id: artistDTO.id as number, ...artistDTO }) };
}

// eslint-disable-next-line no-underscore-dangle
async function _delete(uuid: number | string): Promise<ArtistRecord> {
    const {
        data: { content: isDeleted }
    } = await deleteOne({
        uuid: uuid as string
    });
    assertIsDefined(isDeleted);

    return {} as ArtistRecord;
}

async function deleteMany(params: Readonly<DeleteManyParams>): Promise<DeleteManyResult> {
    await Promise.all(params.ids.map((id: string) => _delete(id)));
    return {
        data: params.ids.map((id: string) => {
            id;
        })
    };
}

function getErrorDetails(_error: unknown, _operation: MpmDataProviderOperationName<ArtistRecord>): ErrorDetails | undefined {
    // eslint-disable-next-line no-undefined
    return undefined;
}

export const ArtistDataProvider: MpmDataProvider<ArtistRecord> = {
    create,
    delete: _delete,
    deleteMany,
    getErrorDetails,
    getList,
    getMany,
    getManyReference,
    getOne: _getOne,
    update,
    updateMany
};
